import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import ImageCompressor from "./components/ImageCompressor";
import ImageResizer from "./components/Resizer";
import ImageFilterEditor from "./components/ImageFilterEditor";
import ImageCollageMaker from "./components/ImageCollageMaker";
import Footer from "./components/Footer";
import FormatConverter from "./components/FormatConvertor";

export default function App() {
  // Keep track of which component is currently active
  const [activeComponent, setActiveComponent] = useState("compressor");

  // Mobile menu anchor
  const [anchorElNav, setAnchorElNav] = useState(null);

  // MUI theme for responsiveness
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Navigation pages
  const pages = [
    { label: "Compressor", value: "compressor" },
    { label: "Resizer", value: "resizer" },
    { label: "Filter Editor", value: "filter" },
    { label: "Collage Maker", value: "collage" },
    { label: "Format Convertor", value: "format" },
  ];

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleNavClick = (value) => {
    setActiveComponent(value);
    setAnchorElNav(null);
  };

  // Render the chosen component
  const renderActiveComponent = () => {
    switch (activeComponent) {
      case "compressor":
        return <ImageCompressor />;
      case "resizer":
        return <ImageResizer />;
      case "filter":
        return <ImageFilterEditor />;
      case "collage":
        return <ImageCollageMaker />;
      case "format":
        return <FormatConverter />;
      default:
        return <ImageCompressor />;
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="sticky"
        sx={{
          background: "linear-gradient(90deg, #0D47A1 0%, #1976D2 100%)",
          opacity: 0.95,
          boxShadow: theme.shadows[4],
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Left side: Brand / Title (desktop) */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                <img
                  src="/logoo.png" // Make sure your file is in the public folder or adjust path as needed
                  alt="IXT Logo"
                  style={{ height: 35, marginRight: 8 }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  ImageXperT
                </Typography>
              </Box>
            )}

            {/* Brand / Title (mobile) */}
            {isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <img
                  src="/ixtLogo.png"
                  alt="IXT Logo"
                  style={{ height: 35, marginRight: 8 }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  ImageXpert
                </Typography>
              </Box>
            )}

            {/* Spacer/flex-grow so items can move to the right */}
            <Box sx={{ flexGrow: 1, display: "flex" }} />

            {isMobile ? (
              // Show Hamburger Menu Icon on Mobile
              <>
                <IconButton
                  size="large"
                  aria-label="menu"
                  onClick={handleOpenNavMenu}
                  sx={{ color: "#fff" }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  keepMounted
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                >
                  {pages.map((page) => (
                    <MenuItem
                      key={page.value}
                      onClick={() => handleNavClick(page.value)}
                    >
                      <Typography textAlign="center">{page.label}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              // Show buttons on desktop
              <Box sx={{ display: "flex", gap: 1 }}>
                {pages.map((page) => (
                  <Button
                    key={page.value}
                    onClick={() => handleNavClick(page.value)}
                    sx={{
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 500,
                      px: 2,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: alpha("#fff", 0.15),
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {page.label}
                  </Button>
                ))}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Container
        maxWidth="md"
        sx={{
          py: 4,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: theme.shadows[2],
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {renderActiveComponent()}
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ mt: "auto" }}>
        <Footer />
      </Box>
    </Box>
  );
}
